import { STORE_KEYS } from "./StoreKeys";
import TaskContextMenu from "@/react/components/entityViews/contextMenus/task/TaskContextMenu";
import ResourceContextMenu from "@/react/components/entityViews/contextMenus/resource/ResourceContextMenu";
import ClientContextMenu from "@/react/components/entityViews/contextMenus/client/ClientContextMenu";
import { EntityDashboardView } from "@/react/components/entityManager/EntityDashboardView";
import { EntityRecord } from "@/types/entity";
import { ENTITY_TYPES, VIEW_TYPES } from "@/other/constants/OtherKeys";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { ArrowUpRight } from "lucide-react";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { SETTING_KEYS } from "@/react/components/entityViews/settings/constants/settingsStructure";

export type EntityTypeKey = (typeof ENTITY_TYPES)[keyof typeof ENTITY_TYPES];

interface TabCallbackParams {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  widget: any;
  isFirstShow: boolean;
  record: EntityRecord;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  context: any;
}

interface ReactTabConfig {
  label: string;
  icon: string;
  type?: never;
  Tab: React.ElementType;
  onDependencyChange?: never;
  onVisible?: never;
}

interface BryntumTabConfig {
  label: string;
  icon: string;
  type: "bryntum";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Tab: any;
  onDependencyChange?: (params: TabCallbackParams) => void;
  onVisible?: (params: TabCallbackParams) => void;
}

type TabConfig = ReactTabConfig | BryntumTabConfig;

// Base pill item config
interface BasePillItemConfig {
  label: string;
  placeholder?: string;
}

// Combobox pill item (default)
interface ComboboxPillItemConfig extends BasePillItemConfig {
  type?: "combobox";
  storeKey: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterFn: (record: any) => boolean;
  showBadges?: boolean;
  multiSelect?: boolean;
  fieldConfig: {
    name: string;
    valueField: string;
    displayField: string;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  disabled?: (record: EntityRecord, context: any) => boolean;
  createOnUnmatched?: (params: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editorHandler?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    viewManager?: any;
    value?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    record?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    project?: any;
  }) => void;
  optionItemActionRenderer?: (params: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    record: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editorHandler: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    entityManager: any;
  }) => React.ReactNode;
}

// Input field pill item
interface InputPillItemConfig extends BasePillItemConfig {
  type: "input";
  fieldName: string;
  inputType?: "text" | "number";
}

// Icon field pill item
interface IconPillItemConfig extends BasePillItemConfig {
  type: "icon";
  storeKey: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterFn: (record: any) => boolean;
  fieldConfig: {
    name: string;
    valueField: string;
    displayField: string;
  };
}

export type PillItemConfig = ComboboxPillItemConfig | InputPillItemConfig | IconPillItemConfig;

export const PILL_ITEMS_MAP: { [K in EntityTypeKey]: PillItemConfig[] } = {
  [ENTITY_TYPES.TASK]: [
    {
      storeKey: STORE_KEYS.taskTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Status",
      label: "Status",
      placeholder: "Status",
      showBadges: true,
      fieldConfig: {
        name: "statusId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatched: ({viewManager}) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: 'Settings', otherParams: {
          setting: SETTING_KEYS.TASK_STATUSES
        }});
      }
    },
    {
      storeKey: STORE_KEYS.taskTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Category",
      label: "Category",
      placeholder: "Category",
      showBadges: true,
      fieldConfig: {
        name: "categoryId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatched: ({viewManager}) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: 'Settings', otherParams: {
          setting: SETTING_KEYS.TASK_CATEGORIES
        }});
      }
    },
    {
      storeKey: STORE_KEYS.boardTeamsStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (_record: any) => true,
      label: "Team",
      placeholder: "Team",
      showBadges: true,
      fieldConfig: {
        name: "teamId",
        valueField: "id",
        displayField: "name"
      },
      createOnUnmatched: ({viewManager}) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: 'Settings', otherParams: {
          setting: SETTING_KEYS.TEAMS_GENERAL
        }});
      }
    },
    {
      storeKey: STORE_KEYS.taskTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Phase",
      label: "Phase",
      placeholder: "Phase",
      showBadges: true,
      fieldConfig: {
        name: "phaseId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatched: ({viewManager}) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: 'Settings', otherParams: {
          setting: SETTING_KEYS.TASK_TAGS
        }});
      }
    },
    {
      storeKey: STORE_KEYS.taskTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Tag",
      label: "Tags",
      placeholder: "Tags",
      multiSelect: true,
      showBadges: true,
      fieldConfig: {
        name: "tagsId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatched: ({viewManager}) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: 'Settings', otherParams: {
          setting: SETTING_KEYS.TASK_PHASE
        }});
      }
    },
    {
      storeKey: STORE_KEYS.clientStore,
      filterFn: () => true,
      label: "Client",
      placeholder: "Client",
      showBadges: true,
      fieldConfig: {
        name: "clientId",
        valueField: "id",
        displayField: "name"
      },
      createOnUnmatched: ({value, record, project}) => {
        const newRecord = project.clientsStore.createRecord({
          name: value
        });
        project.clientsStore.add(newRecord);
        record.set("clientId", newRecord.id);
      },
      optionItemActionRenderer: ({record, entityManager}) => {
        return <button type="button" className="opacity-0 group-hover/command-item:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-sm p-1 transition-opacity" onClick={(e) => {
          e.stopPropagation();
          entityManager.openEntityManager({ entityType: ENTITY_TYPES.CLIENT, entityId: record.id });
        }}>
        <ArrowUpRight className="size-3" />
          </button>
      }
    },
    {
      storeKey: STORE_KEYS.taskBudgetStore,
      filterFn: () => true,
      disabled: (_record, context) => !context?.user?.can?.(GRANTS.grant_two),
      label: "Budget",
      placeholder: "Budget",
      showBadges: true,
      fieldConfig: {
        name: "budgetId",
        valueField: "id",
        displayField: "budgetTitleDisplayValue"
      },
      createOnUnmatched: ({editorHandler, project}) => {
        const newRecord = project.taskBudgetStore.createRecord();
        editorHandler.openEditor(EDITOR_KEYS.budgetEditor, newRecord, { budget: newRecord });
      },
      optionItemActionRenderer: ({record, editorHandler}) => {
        return <button type="button" className="opacity-0 group-hover/command-item:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-sm p-1 transition-opacity" onClick={(e) => {
          e.stopPropagation();
          editorHandler.openEditor(EDITOR_KEYS.budgetEditor, record, { budget: record });
        }}>
        <ArrowUpRight className="size-3" />
          </button>
      }
    }
  ],
  [ENTITY_TYPES.RESOURCE]: [
    {
      storeKey: STORE_KEYS.resourceTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Status",
      label: "Status",
      placeholder: "Status",
      showBadges: true,
      fieldConfig: {
        name: "statusId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatched: ({viewManager}) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: 'Settings', otherParams: {
          setting: SETTING_KEYS.RESOURCE_STATUSES
        }});
      }
    },
    {
      storeKey: STORE_KEYS.resourceTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Category",
      label: "Category",
      placeholder: "Category",
      showBadges: true,
      fieldConfig: {
        name: "categoryId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatched: ({viewManager}) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: 'Settings', otherParams: {
          setting: SETTING_KEYS.RESOURCE_CATEGORIES
        }});
      }
    },
    {
      storeKey: STORE_KEYS.boardTeamsStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (_record: any) => true,
      label: "Team",
      placeholder: "Team",
      showBadges: true,
      fieldConfig: {
        name: "teamId",
        valueField: "id",
        displayField: "name"
      },
      createOnUnmatched: ({viewManager}) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: 'Settings', otherParams: {
          setting: SETTING_KEYS.TEAMS_GENERAL
        }});
      }
    },
    {
      storeKey: STORE_KEYS.resourceTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Tag",
      label: "Tags",
      placeholder: "Tags",
      multiSelect: true,
      showBadges: true,
      fieldConfig: {
        name: "tagsId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatched: ({viewManager}) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: 'Settings', otherParams: {
          setting: SETTING_KEYS.RESOURCE_TAGS
        }});
      }
    },
    {
      type: "icon",
      storeKey: STORE_KEYS.resourceTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Icon",
      label: "Icon",
      placeholder: "Icon",
      fieldConfig: {
        name: "iconId",
        valueField: "id",
        displayField: "tagValue"
      }
    },
    {
      storeKey: STORE_KEYS.resourceTypeStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (_record: any) => true,
      label: "Type",
      placeholder: "Type",
      showBadges: true,
      fieldConfig: {
        name: "typeId",
        valueField: "id",
        displayField: "description"
      },
      createOnUnmatched: ({viewManager}) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: 'Settings', otherParams: {
          setting: SETTING_KEYS.RESOURCE_TYPES
        }});
      }
    },
    {
      type: "input",
      label: "Sub Type",
      placeholder: "Add Sub Type",
      fieldName: "jobTitle",
      inputType: "text"
    }
  ],
  [ENTITY_TYPES.CLIENT]: [
    {
      storeKey: STORE_KEYS.clientTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Status",
      label: "Status",
      placeholder: "Status",
      showBadges: true,
      fieldConfig: {
        name: "statusId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatched: ({viewManager}) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: 'Settings', otherParams: {
          setting: SETTING_KEYS.CLIENT_STATUSES
        }});
      }
    },
    {
      storeKey: STORE_KEYS.clientTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Category",
      label: "Category",
      placeholder: "Category",
      showBadges: true,
      fieldConfig: {
        name: "categoryId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatched: ({viewManager}) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: 'Settings', otherParams: {
          setting: SETTING_KEYS.CLIENT_CATEGORIES
        }});
      }
    },
    {
      storeKey: STORE_KEYS.boardTeamsStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (_record: any) => true,
      label: "Team",
      placeholder: "Team",
      showBadges: true,
      fieldConfig: {
        name: "teamId",
        valueField: "id",
        displayField: "name"
      },
      createOnUnmatched: ({viewManager}) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: 'Settings', otherParams: {
          setting: SETTING_KEYS.TEAMS_GENERAL
        }});
      }
    },
    {
      storeKey: STORE_KEYS.clientTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Tag",
      label: "Tags",
      placeholder: "Tags",
      multiSelect: true,
      showBadges: true,
      fieldConfig: {
        name: "tagsId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatched: ({viewManager}) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: 'Settings', otherParams: {
          setting: SETTING_KEYS.CLIENT_TAGS
        }});
      }
    }
  ]
};

export interface EntityConfig {
  storeKey: string;
  tabs: TabConfig[];
  contextMenu?: React.ElementType;
  pillItems: PillItemConfig[];
}

export const ENTITY_CONFIGS: Record<EntityTypeKey, EntityConfig> = {
  [ENTITY_TYPES.TASK]: {
    storeKey: STORE_KEYS.taskStore,
    contextMenu: TaskContextMenu,
    pillItems: PILL_ITEMS_MAP.task,
    tabs: [
      {
        label: "Dashboard",
        icon: "fa-regular fa-house",
        Tab: EntityDashboardView
      },
    ]
  },
  [ENTITY_TYPES.RESOURCE]: {
    storeKey: STORE_KEYS.resourceStore,
    contextMenu: ResourceContextMenu,
    pillItems: PILL_ITEMS_MAP.resource,
    tabs: [
      {
        label: "Dashboard",
        icon: "fa-regular fa-house",
        Tab: EntityDashboardView
      },
    ]
  },
  [ENTITY_TYPES.CLIENT]: {
    storeKey: STORE_KEYS.clientStore,
    contextMenu: ClientContextMenu,
    pillItems: PILL_ITEMS_MAP.client,
    tabs: [
      {
        label: "Dashboard",
        icon: "fa-regular fa-house",
        Tab: EntityDashboardView
      },
    ]
  }
};
